/**
 * ManagerAccessPage — Phase D1.
 *
 * Artist owners (`user.user_type === 'domain-admin'` AND `user.domain` set)
 * can grant manager access to their own domain by emailing an invite.
 *
 * Reuses the same Invitation + OrganizationManagerAssignment pipeline as
 * org-mediated access. There is no organization, no role selector (manager
 * only), and no domain selector (the owner's own slug is implicit).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Mail, Plus, Trash2, Users, Copy } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';

import {
  managerAccessApi,
  adminManagerAccessApi,
  type DirectManager,
  type DirectInvitation,
} from '@/services/managerAccessApi';

interface ManagerAccessPageProps {
  /** When provided (super-admin view), operations target this slug via admin API. */
  domainSlug?: string;
  userType?: string;
}

const ManagerAccessPage: React.FC<ManagerAccessPageProps> = ({ domainSlug, userType }) => {
  const isAdminView = userType === 'super-admin' && !!domainSlug;
  const [loading, setLoading] = useState(true);
  const [slug, setSlug] = useState('');
  const [managers, setManagers] = useState<DirectManager[]>([]);
  const [invitations, setInvitations] = useState<DirectInvitation[]>([]);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareEmail, setShareEmail] = useState('');

  const [confirmRevokeManager, setConfirmRevokeManager] = useState<DirectManager | null>(null);
  const [confirmRevokeInvite, setConfirmRevokeInvite] = useState<DirectInvitation | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = isAdminView
        ? await adminManagerAccessApi.list(domainSlug!)
        : await managerAccessApi.list();
      setSlug(data.slug);
      setManagers(data.managers);
      setInvitations(data.invitations);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to load managers');
    } finally {
      setLoading(false);
    }
  }, [isAdminView, domainSlug]);

  useEffect(() => { void reload(); }, [reload]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleaned = email.trim().toLowerCase();
    if (!cleaned) return;
    setSubmitting(true);
    try {
      const result = isAdminView
        ? await adminManagerAccessApi.invite(domainSlug!, cleaned)
        : await managerAccessApi.invite(cleaned);
      setInviteOpen(false);
      setEmail('');
      if (result.granted) {
        if (result.alreadyHadAccess) {
          toast.success(`${cleaned} already had access`);
        } else {
          toast.success(`Access granted to ${cleaned}`);
        }
      } else if (result.token) {
        setShareEmail(cleaned);
        setShareUrl(`${window.location.origin}/invite/${result.token}`);
        toast.success('Invitation emailed');
      } else if (result.inviteUrl) {
        setShareEmail(cleaned);
        setShareUrl(result.inviteUrl.startsWith('http') ? result.inviteUrl : `${window.location.origin}${result.inviteUrl}`);
        toast.success('Invitation emailed');
      } else {
        toast.success('Invitation emailed');
      }
      await reload();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to send invite');
    } finally {
      setSubmitting(false);
    }
  };

  const copyShareUrl = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(`Invite link copied for ${shareEmail}`);
    } catch {
      toast.error('Copy failed. Select the link below and copy manually.');
    }
  };

  const doRevokeManager = async () => {
    if (!confirmRevokeManager) return;
    try {
      if (isAdminView) {
        await adminManagerAccessApi.revokeManager(domainSlug!, confirmRevokeManager.userId);
      } else {
        await managerAccessApi.revokeManager(confirmRevokeManager.userId);
      }
      toast.success('Manager access revoked');
      setConfirmRevokeManager(null);
      await reload();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to revoke');
    }
  };

  const doRevokeInvite = async () => {
    if (!confirmRevokeInvite) return;
    try {
      if (isAdminView) {
        await adminManagerAccessApi.revokeInvitation(domainSlug!, confirmRevokeInvite._id);
      } else {
        await managerAccessApi.revokeInvitation(confirmRevokeInvite._id);
      }
      toast.success('Invitation revoked');
      setConfirmRevokeInvite(null);
      await reload();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to revoke');
    }
  };

  if (loading) {
    return (
      <div className="p-4 lg:p-8 flex items-center justify-center min-h-[40vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8 space-y-8">
      <div>
        <h1 className="page-title">Manager Access</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Invite trusted people to manage <span className="font-semibold text-foreground">{slug}</span> on
          your behalf. Managers sign in with their own account and only get access to your domain.
        </p>
      </div>

      {/* Active managers */}
      <section>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
          <h3 className="text-section-title">Managers ({managers.length})</h3>
          <Button onClick={() => setInviteOpen(true)} className="gap-1">
            <Plus className="w-4 h-4" /> Invite Manager
          </Button>
        </div>

        {managers.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <Users className="w-full h-full" />
            </div>
            <h3 className="empty-state-title">No managers yet</h3>
            <p className="empty-state-description">
              Use Invite Manager to grant someone access to your domain.
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="text-left">
              {managers.map((m) => {
                const viaOrg = m.source === 'organization' && m.organization;
                return (
                  <TableRow key={m._id}>
                    <TableCell>
                      <div className="font-medium">{m.fullName || '—'}</div>
                      <div className="text-xs text-muted-foreground">{m.email}</div>
                      {viaOrg && (
                        <div className="text-xs text-muted-foreground italic mt-0.5">
                          Managed by {m.organization!.name}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="capitalize">Manager</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">active</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {viaOrg ? (
                        <span className="text-xs text-muted-foreground">Org-managed</span>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setConfirmRevokeManager(m)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </section>

      {/* Pending invitations */}
      <section>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
          <h3 className="text-section-title">Pending Invitations ({invitations.length})</h3>
        </div>

        {invitations.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <Mail className="w-full h-full" />
            </div>
            <h3 className="empty-state-title">No pending invitations</h3>
            <p className="empty-state-description">
              Invitations you send will appear here until they're accepted.
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="text-left">
              {invitations.map((inv) => (
                <TableRow key={inv._id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      {inv.email}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="capitalize">{inv.invitedRole || 'manager'}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmRevokeInvite(inv)}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </section>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Manager</DialogTitle>
            <DialogDescription>
              They'll receive an email with a secure link to sign up and gain manager access to your domain.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInvite} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="manager@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInviteOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? <Spinner size="sm" className="mr-2 text-inherit" /> : null}
                Send Invite
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Share URL dialog (shown after successful invite) */}
      <Dialog open={!!shareUrl} onOpenChange={(o) => !o && setShareUrl(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invitation sent</DialogTitle>
            <DialogDescription>
              Email sent to <span className="font-semibold text-foreground">{shareEmail}</span>. You can also share this link directly.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Input value={shareUrl || ''} readOnly className="font-mono text-xs" />
              <Button type="button" size="sm" variant="outline" onClick={copyShareUrl}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShareUrl(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke manager confirm */}
      <ConfirmDialog
        open={!!confirmRevokeManager}
        onOpenChange={(o) => !o && setConfirmRevokeManager(null)}
        title="Revoke manager access?"
        description={`${confirmRevokeManager?.fullName || confirmRevokeManager?.email || ''} will immediately lose access to your domain. They keep their account.`}
        confirmLabel="Revoke"
        onConfirm={doRevokeManager}
      />

      {/* Revoke invite confirm */}
      <ConfirmDialog
        open={!!confirmRevokeInvite}
        onOpenChange={(o) => !o && setConfirmRevokeInvite(null)}
        title="Revoke invitation?"
        description={`The invite link sent to ${confirmRevokeInvite?.email || ''} will stop working immediately.`}
        confirmLabel="Revoke"
        onConfirm={doRevokeInvite}
      />
    </div>
  );
};

export default ManagerAccessPage;
